import { useCallback, useEffect, useState } from 'react'
import { UserPlus, Loader2, Check, X } from 'lucide-react'
import {
  listAccessRequests,
  approveAccessRequest,
  denyAccessRequest,
  type AccessRequest,
} from '../lib/api'

/**
 * Surfaces pending access requests on apps the current user owns, with Approve /
 * Deny actions. Self-contained (fetches its own data, renders nothing when
 * empty) so it can be dropped onto both Home and My Apps alongside
 * IncomingTransfersBanner.
 */
export default function AccessRequestsBanner() {
  const [requests, setRequests] = useState<AccessRequest[]>([])
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRequests(await listAccessRequests())
    } catch {
      // transient / unauthenticated — keep the banner silent
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function act(request: AccessRequest, fn: (id: number) => Promise<unknown>, verb: string) {
    setBusyId(request.id)
    setError(null)
    try {
      await fn(request.id)
      setRequests((prev) => prev.filter((r) => r.id !== request.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${verb} request`)
    } finally {
      setBusyId(null)
    }
  }

  if (requests.length === 0) return null

  return (
    <div className="mb-6 space-y-2">
      {requests.map((req) => (
        <div
          key={req.id}
          className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-purple-500/40 bg-purple-500/10"
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <UserPlus className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
            <div className="min-w-0 text-sm">
              <p className="font-medium">
                {req.requester_name || req.requester_email} requested access to{' '}
                <strong>{req.app_display_name || req.app_name}</strong>.
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Approving grants them viewer access to this app.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => act(req, denyAccessRequest, 'deny')}
              disabled={busyId === req.id}
              className="px-3 py-1.5 rounded-lg border border-border hover:bg-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busyId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Deny
            </button>
            <button
              onClick={() => act(req, approveAccessRequest, 'approve')}
              disabled={busyId === req.id}
              className="px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-600/90 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busyId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Approve
            </button>
          </div>
        </div>
      ))}
      {error && <div className="p-2 rounded-lg bg-destructive/10 text-destructive text-xs">{error}</div>}
    </div>
  )
}
